/**
 * @GestionalePD Admin region federation UI
 */
import axios from 'axios';
import { useNavigate } from 'react-router';
import React, { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import {
  Paper,
  TableContainer,
  TableBody,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Box,
  TextField,
  TableFooter,
  TablePagination,
  Container,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  useMediaQuery,
  Stack,
  FormControlLabel,
  Switch,
  Typography,
  Tooltip,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddIcon from '@mui/icons-material/Add';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
//------------------------------------------------------------------------------------------------
import { usePopup } from '../../../../context/PopupContext';
import {
  DefaultContainedButton,
  DefaultOutlinedButton,
  CustomIconButton,
  SearchIconButton,
} from '../../../../utils/Buttons';
import { CustomAutocomplete, CustomTextField } from '../../../../utils/StyledInputs';
import { HttpErrorHandler, ErrorUI } from '../../../../utils/ErrorHandler';
import { LoadingUI } from '../../../../utils/LayoutStyles';
import { TablePaginationActions } from '../../../../utils/TableStyles';
import { validatePhoneNumber } from '../../../../utils/numberValidity';
import { entityTypeLabel } from '../../../../utils/EntityTypeLabel';
import API from '../../../../api';

/**
 * @returns Admin region federation UI
 */
export default function RegionalFederation() {
  // general
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const navigate = useNavigate();
  const { state } = useLocation();
  const { federationID } = state;
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setLoading] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [currentProfile, setCurrentProfile] = useState('');
  const [canModifyEntity, setCanModifyEntity] = useState(false);
  const [canViewUserIcon, setCanViewUserIcon] = useState(false);
  const [italy, setItaly] = useState({});

  // organization entity data
  const [entityTypeID, setEntityTypeID] = useState('');
  const [entities, setEntities] = useState([]);
  const [entityData, setEntityData] = useState({});
  const [entityType, setEntityType] = useState([]);
  const [isEntityLoading, setIsEntityLoading] = useState(false);

  // popup handler
  const { showPopup, setPopUpText } = usePopup();

  // table paginatin handler
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - entities.length) : 0;
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // search bar handler
  const [searchedInput, setSearchedInput] = useState('');
  const [blockSearchRefresh, setBlockSearchRefresh] = useState(false);

  const handleSearchInputChange = (e) => {
    e.preventDefault();
    setPage(0);

    if (searchedInput !== '') {
      API.get(`/entities/${federationID}?expand=children&search=${searchedInput}`)
        .then((response) => {
          setEntityData(response.data);
          setEntities(response.data.child_entities);
        })
        .catch((error) => {
          HttpErrorHandler(error, setErrorMessage);
        });

      setBlockSearchRefresh(false);
    } else {
      if (!blockSearchRefresh) {
        refreshFederations();
      }
      setBlockSearchRefresh(true);
    }
  };

  // address api list declaration
  const [cityList, setCityList] = useState([]);
  const [provinceList, setProvinceList] = useState([]);
  const [regionList, setRegionList] = useState([]);
  const [countryList, setCountryList] = useState([]);

  // new federation
  const [federationName, setfederationName] = useState('');
  const [federationShortName, setfederationShortName] = useState('');
  const [federationEmail, setfederationEmail] = useState('');
  const [federationCallCode, setfederationCallCode] = useState('');
  const [federationPhone, setfederationPhone] = useState('');
  const [federationStreet, setfederationStreet] = useState('');
  const [federationPostalCode, setFederationPostalCode] = useState('');
  const [federationCity, setFederationCity] = useState(null);
  const [federationCityName, setFederationCityName] = useState('');
  const [federationProvince, setFederationProvince] = useState(null);
  const [federationRegionName, setFederationRegionName] = useState('');
  const [federationRegion, setFederationRegion] = useState(null);
  const [federationCountry, setFederationCountry] = useState(null);
  const [entityIsMaster, setEntityIsMaster] = useState(false);

  // open new federation dialog
  const [openNewFederationDialog, setOpenNewFederationDialog] = useState(false);
  const onOpenNewFederationDialog = () => {
    setOpenNewFederationDialog(true);
    // getAdressApi();
    getEntityTypes();
  };

  // close new federation dialog
  const onCloseNewFederationDialog = () => {
    setOpenNewFederationDialog(false);
    setFieldErrors({});
  };

  // create new federation
  const onCreateFederation = (event) => {
    event.preventDefault();

    const federationData = {
      entity_type_id: entityTypeID ? entityTypeID.id : null,
      name: federationName,
      country_id: federationCountry ? federationCountry.id : null,
      master: entityIsMaster ? 1 : 0,
    };

    // optional fields: include only if they have values
    if (federationShortName) federationData.short_name = federationShortName;
    if (federationEmail) federationData.email = federationEmail;
    if (federationCallCode) federationData.call_code = federationCallCode.call_code;
    if (federationPhone) federationData.phone = federationPhone;
    if (federationStreet) federationData.street = federationStreet;
    if (federationPostalCode) federationData.zip_code = federationPostalCode;

    if (federationData.country_id === italy.id) {
      federationData.province_id = federationProvince ? federationProvince.id : null;
      federationData.city_id = federationCity ? federationCity.id : null;
    } else {
      federationData.region_name = federationRegionName;
      federationData.city_name = federationCityName;
    }

    // validate only required fields: name, entity type, country, region, province, city
    const errors = {};

    if (!federationName) errors.name = true;
    if (!entityTypeID) errors.entity_type_id = true;
    if (!federationCountry) errors.country_id = true;

    if (federationCountry && federationCountry.id === italy.id) {
      if (!federationRegion) errors.region = true;
      if (!federationProvince) errors.province_id = true;
      if (!federationCity) errors.city_id = true;
    } else {
      if (!federationRegionName) errors.region_name = true;
      if (!federationCityName) errors.city_name = true;
    }

    setFieldErrors(errors);
    const itemsLeft = Object.keys(errors).length > 0;

    if (itemsLeft) {
      showPopup('warning', 'Inserire i dati mancanti!');
    }

    if (!itemsLeft) {
      // parent_id: consente al backend di verificare OrgStructure/Write sul padre PRIMA di creare
      // l'entità, evitando entità orfane se l'utente non è autorizzato su questa federazione.
      API.post(`/entities`, { ...federationData, parent_id: federationID })
        .then((response) => {
          createRelation(response.data.entity.id);
          onCloseNewFederationDialog();
          refreshNewFederationDialog();
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  // create new entity relations federazione provinciale -> circolo
  const createRelation = (id) => {
    const relData = {
      parent_id: federationID,
      entity_id: id,
    };

    API.post(`/erels`, relData)
      .then((response) => {
        refreshFederations();
        showPopup(true, 'Informazioni salvate');
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // refresh new federation dialog
  const refreshNewFederationDialog = () => {
    setfederationName('');
    setfederationShortName('');
    setfederationEmail('');
    setfederationCallCode('');
    setfederationPhone('');
    setfederationStreet('');
    setFederationPostalCode('');
    setFederationCity('');
    setFederationProvince('');
    setFederationRegion('');
    setFederationCountry('');
  };

  // refresh displayed federations
  const refreshFederations = () => {
    setErrorMessage('');
    setIsEntityLoading(true);
    API.get(`/entities/${federationID}?expand=children`)
      .then((response) => {
        setEntityData(response.data);
        setEntities(response.data.child_entities);

        setCanModifyEntity(response.data.auth_type === 'W' || response.data.auth_type === 'D');
      })
      .catch((error) => HttpErrorHandler(error, setErrorMessage))
      .finally(() => {
        setLoading(false);
        setIsEntityLoading(false);
        setPage(0);
      });
  };

  useEffect(() => {
    if (openNewFederationDialog) {
      API.get(`/countries`).then((response) => {
        setCountryList(response.data);
        const objectItaly = response.data.find((country) => country.name === 'Italia');
        setItaly(objectItaly);
        setFederationCountry(objectItaly);
        setfederationCallCode(objectItaly);
      });
    }
  }, [openNewFederationDialog]);

  //   Get Regions List
  useEffect(() => {
    if (federationCountry !== null && federationCountry !== undefined && federationCountry !== '') {
      if (typeof federationCountry === 'object' && Object.keys(federationCountry).length > 0) {
        API.get(`/countries/regions`, {
          params: {
            id: federationCountry.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setRegionList(response.data);
          } else {
            setRegionList([]);
            setProvinceList([]);
            setCityList([]);
          }
        });
      }
    }
  }, [federationCountry]);

  //   Get Provinces List
  useEffect(() => {
    if (federationRegion !== null && federationRegion !== undefined && federationRegion !== '') {
      if (typeof federationRegion === 'object' && Object.keys(federationRegion).length > 0) {
        API.get(`/regions/provinces`, {
          params: {
            region_name: federationRegion.region_name,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setProvinceList(response.data);
          } else {
            setProvinceList([]);
            setCityList([]);
          }
        });
      }
    }
  }, [federationRegion]);

  //   Get Cities List
  useEffect(() => {
    if (federationProvince !== null && federationProvince !== undefined && federationProvince !== '') {
      if (typeof federationProvince === 'object' && Object.keys(federationProvince).length > 0) {
        API.get(`/provinces/cities`, {
          params: {
            id: federationProvince.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setCityList(response.data);
          } else {
            setCityList([]);
          }
        });
      }
    }
  }, [federationProvince]);

  // get api call for entity types
  const getEntityTypes = () => {
    setErrorMessage('');

    API.get('/etypes')
      .then((response) => {
        const entityTypeList = response.data.entity_types.filter((type) => type.level !== 0);
        setEntityType(entityTypeList);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // initial data loading
  useEffect(() => {
    refreshFederations();
  }, [federationID]);

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
      setCanViewUserIcon(data.can_view_users);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  // if federation id not found navigate to 404 page
  if (!federationID) return <Navigate to={{ pathname: '/404' }} />;

  return !isLoading ? (
    errorMessage.length === 0 ? (
      <Container maxWidth="xl">
        <Box role="presentation" sx={{ marginBottom: '24px' }}>
          <Grid item xs={12}>
            {/* New federation button */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography
                sx={{
                  height: '24px',
                  width: 'fit-content',
                  whiteSpace: 'nowrap',
                  borderRadius: '6px',
                  padding: '0 10px',
                  textAlign: 'center',
                  fontWeight: '700',
                  fontSize: '1.1em',
                }}
              >
                {entityData.name}
              </Typography>
              <DefaultContainedButton
                onClick={onOpenNewFederationDialog}
                customStyles={{ display: canModifyEntity ? 'flex' : 'none' }}
                startIcon={<AddIcon />}
              >
                Nuova Entità
              </DefaultContainedButton>
            </Box>

            {/* new federation dialog */}
            <Dialog open={openNewFederationDialog} fullWidth onClose={() => {}}>
              <form onSubmit={onCreateFederation}>
                {/* new federation dialog title */}
                <DialogTitle textAlign="center">Nuova Entità</DialogTitle>

                {/* new federation dialog content */}
                <DialogContent>
                  {countryList.length !== 0 ? (
                    <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                      {/* federation name */}
                      <Grid item xs={12}>
                        <CustomTextField
                          required
                          fullWidth
                          margin="dense"
                          id="federation-name"
                          value={federationName}
                          onChange={(e) => setfederationName(e.currentTarget.value)}
                          label="Nome"
                          type="text"
                          error={!!fieldErrors.name}
                        />
                      </Grid>

                      {/* federation short name */}
                      <Grid item xs={12}>
                        <CustomTextField
                          fullWidth
                          margin="dense"
                          id="federation-short-name"
                          value={federationShortName}
                          onChange={(e) => setfederationShortName(e.currentTarget.value)}
                          label="Nome in breve"
                          type="text"
                        />
                      </Grid>

                      {/* federation email */}
                      <Grid item xs={12}>
                        <CustomTextField
                          fullWidth
                          margin="dense"
                          id="federation-email"
                          value={federationEmail}
                          onChange={(e) => setfederationEmail(e.currentTarget.value)}
                          label="E-mail"
                          type="email"
                        />
                      </Grid>

                      {/* federation entity type */}
                      <Grid item xs={12}>
                        <CustomAutocomplete
                          required
                          fullWidth
                          id="federation-entity-type"
                          options={entityType}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => option.name}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          onChange={(e, value) => setEntityTypeID(value)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Tipo entità *"
                              variant="outlined"
                              error={!!fieldErrors.entity_type_id}
                            />
                          )}
                        />
                      </Grid>

                      {/* federation call code */}
                      <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
                        <CustomAutocomplete
                          id="federation-call-code"
                          options={countryList}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => option.call_code}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name} {option.call_code}
                            </Box>
                          )}
                          value={federationCallCode}
                          filterOptions={(options, state) => {
                            const newOptions = [];

                            options.forEach((element) => {
                              if (
                                element.name.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                                element.call_code.toLowerCase().includes(state.inputValue.toLowerCase())
                              ) {
                                newOptions.push(element);
                              }
                            });

                            return newOptions;
                          }}
                          onChange={(e, value) => setfederationCallCode(value)}
                          renderInput={(params) => <TextField {...params} label="Prefisso" variant="outlined" />}
                        />
                      </Grid>

                      {/* federation phone */}
                      <Grid item xs={8}>
                        <CustomTextField
                          fullWidth
                          margin="dense"
                          id="federation-phone"
                          value={federationPhone}
                          onChange={(event) => validatePhoneNumber(event, setfederationPhone)}
                          label="Numero Contatto"
                          type="tel"
                        />
                      </Grid>

                      {/* federation street */}
                      <Grid item xs={9}>
                        <CustomTextField
                          fullWidth
                          margin="dense"
                          id="federation-street"
                          value={federationStreet}
                          onChange={(e) => setfederationStreet(e.currentTarget.value)}
                          label="Indirizzo"
                          type="text"
                        />
                      </Grid>

                      {/* federation postal code */}
                      <Grid item xs={3}>
                        <CustomTextField
                          fullWidth
                          margin="dense"
                          id="federation-postal-code"
                          value={federationPostalCode}
                          onChange={(e) => setFederationPostalCode(e.currentTarget.value)}
                          label="CAP"
                          type="text"
                        />
                      </Grid>

                      {/* federation country */}
                      <Grid item xs={6}>
                        <CustomAutocomplete
                          id="federation-country"
                          options={countryList.filter(
                            (country) =>
                              (!federationProvince || country.id === federationProvince.country_id) &&
                              (!federationRegion || country.id === federationRegion.country_id)
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => option.name}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          value={federationCountry}
                          onChange={(e, value) => setFederationCountry(value)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Paese *"
                              variant="outlined"
                              error={!!fieldErrors.country_id}
                            />
                          )}
                        />
                      </Grid>

                      {/* federation region */}
                      <Grid item xs={6}>
                        <CustomAutocomplete
                          freeSolo
                          forcePopupIcon={federationCountry === italy}
                          id="federation-region"
                          options={regionList.filter(
                            (region) =>
                              (!federationProvince || region.region_name === federationProvince.region_name) &&
                              (!federationCountry || region.country_id === federationCountry.id)
                          )}
                          isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                              return option;
                            }
                            if (option.inputValue) {
                              return option.inputValue;
                            }
                            return option.region_name;
                          }}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.region_name}>
                              {option.region_name}
                            </Box>
                          )}
                          onChange={(e, value) => {
                            if (typeof value === 'string') {
                              setFederationRegion(value);
                            } else if (value && value.inputValue) {
                              setFederationRegion(value.inputValue);
                            } else {
                              setFederationRegion(value);
                            }
                          }}
                          onInputChange={(e, value) => {
                            setFederationRegionName(value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Regione *"
                              variant="outlined"
                              error={!!(fieldErrors.region || fieldErrors.region_name)}
                            />
                          )}
                        />
                      </Grid>

                      {/* federation province */}
                      <Grid item xs={6}>
                        <CustomAutocomplete
                          disabled={federationCountry !== italy}
                          forcePopupIcon={federationCountry === italy}
                          id="federation-province"
                          options={provinceList.filter(
                            (province) =>
                              (!federationCountry || province.country_id === federationCountry.id) &&
                              (!federationRegion || province.region_name === federationRegion.region_name) &&
                              (!federationCity || province.id === federationCity.province_id)
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                              return option;
                            }
                            if (option.inputValue) {
                              return option.inputValue;
                            }
                            return option.name;
                          }}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          onChange={(e, value) => {
                            if (typeof value === 'string') {
                              setFederationProvince(value);
                            } else if (value && value.inputValue) {
                              setFederationProvince(value.inputValue);
                            } else {
                              setFederationProvince(value);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Provincia *"
                              variant="outlined"
                              error={!!fieldErrors.province_id}
                            />
                          )}
                        />
                      </Grid>

                      {/* federation city */}
                      <Grid item xs={6}>
                        <CustomAutocomplete
                          freeSolo
                          forcePopupIcon={federationCountry === italy}
                          id="federation-city"
                          options={cityList.filter((city) =>
                            federationProvince ? city.province_id === federationProvince.id : true
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                              return option;
                            }
                            if (option.inputValue) {
                              return option.inputValue;
                            }
                            return option.name;
                          }}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          onChange={(e, value) => {
                            if (typeof value === 'string') {
                              setFederationCity(value);
                            } else if (value && value.inputValue) {
                              setFederationCity(value.inputValue);
                            } else {
                              setFederationCity(value);
                            }
                          }}
                          onInputChange={(e, value) => {
                            setFederationCityName(value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Comune *"
                              variant="outlined"
                              error={!!(fieldErrors.city_id || fieldErrors.city_name)}
                            />
                          )}
                        />
                      </Grid>

                      {/* entity master */}
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch checked={entityIsMaster} onChange={() => setEntityIsMaster(!entityIsMaster)} />
                          }
                          label="Eleggibile per il tesseramento"
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    LoadingUI()
                  )}
                </DialogContent>

                {/* new federation dialog footer */}
                <DialogActions>
                  <DefaultOutlinedButton onClick={onCloseNewFederationDialog}>Chiudi</DefaultOutlinedButton>
                  {countryList.length !== 0 && <DefaultContainedButton type="submit">Crea</DefaultContainedButton>}
                </DialogActions>
              </form>
            </Dialog>
          </Grid>
        </Box>

        <Card sx={{ padding: '24px 24px 6px' }}>
          {/* Search field */}

          <Box sx={{ margin: '0px 0px 20px' }}>
            <form onSubmit={(e) => handleSearchInputChange(e)}>
              <Stack
                direction="row"
                alignItems="center"
                gap="20px"
                sx={{
                  marginTop: '10px',
                  marginBottom: '12px',
                }}
              >
                <CustomTextField
                  fullWidth
                  size="small"
                  type="search"
                  placeholder="Cerca ..."
                  onChange={(e) => setSearchedInput(e.currentTarget.value)}
                />

                <SearchIconButton onClick={(e) => handleSearchInputChange(e)}>
                  <SearchIcon />
                </SearchIconButton>
              </Stack>
            </form>
          </Box>

          {/* <Box sx={{ margin: '0px 0px 20px' }}>
            <CustomTextField
              fullWidth
              id="region-org-search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Cerca..."
              onChange={onSearchRequest}
            />
          </Box> */}

          {/* regional federation table */}
          <TableContainer component={Paper} sx={{ height: '100%' }}>
            <Table size="small" aria-label="Federation table">
              {/* regional federation table header */}
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '200px' }}>Tipo Entità</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Regione</TableCell>
                  <TableCell sx={{ width: '300px' }} />
                </TableRow>
              </TableHead>

              {/* regional federation table body */}
              {!isEntityLoading && entities.length !== 0 ? (
                <TableBody>
                  {entities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((entity, index) => (
                    <TableRow hover key={`${entity.id}${index}`}>
                      <TableCell sx={{ width: '200px' }}>{entityTypeLabel(entity, true)}</TableCell>
                      <TableCell>{entity.name}</TableCell>
                      <TableCell>{entity.province?.region_name ?? entity.region_name}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" justifyContent="end" gap="12px">
                          <Tooltip title="Anagrafica" arrow>
                            <Box>
                              <CustomIconButton
                                onClick={() =>
                                  navigate('/admin/organization/federations/federation-details', {
                                    state: { federationID: entity.id },
                                  })
                                }
                              >
                                <DescriptionOutlinedIcon />
                              </CustomIconButton>
                            </Box>
                          </Tooltip>

                          <Tooltip title="Sottolivelli" arrow sx={{ display: entity.master === 0 ? 'flex' : 'none' }}>
                            <Box>
                              <CustomIconButton
                                onClick={() =>
                                  navigate('/admin/organization/federations/federation-child', {
                                    state: { federationID: entity.id },
                                  })
                                }
                              >
                                <AccountTreeOutlinedIcon />
                              </CustomIconButton>
                            </Box>
                          </Tooltip>

                          <Tooltip
                            title="Iscritti"
                            arrow
                            sx={{ display: entity.master === 1 && canViewUserIcon ? 'flex' : 'none' }}
                          >
                            <Box>
                              <CustomIconButton
                                onClick={() =>
                                  navigate(`/admin/organization/federations/users`, { state: { clubID: entity.id } })
                                }
                              >
                                <PeopleAltOutlinedIcon />
                              </CustomIconButton>
                            </Box>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {emptyRows > 0 && (
                    // regional federation table empty row
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={4} />
                    </TableRow>
                  )}
                </TableBody>
              ) : entities.length === 0 ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center' }}>
                      Nessuna entità trovata
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center' }}>
                      {LoadingUI()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}

              {/* regional federation table footer */}
              <TableFooter>
                <TableRow>
                  <TablePagination
                    sx={{ borderBottom: 'none' }}
                    rowsPerPageOptions={[]}
                    colSpan={6}
                    count={entities.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: {
                        'aria-label': 'federation per page',
                      },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Card>

      </Container>
    ) : (
      ErrorUI(errorMessage)
    )
  ) : (
    LoadingUI()
  );
}
